import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { useSendLeadEmail } from "@/hooks/useLeads";
import { cn } from "@/lib/utils";

interface EmailComposerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lead: {
    id: string;
    name: string;
    /** Null when the lead has no e-mail yet: sending is blocked. */
    email: string | null;
  };
  /** Public PDF link (e.g. the proposal) — shown as an attachment chip and appended to the body on send. */
  attachment?: { name: string; url: string } | null;
}

const templates = [
  {
    id: "proposal",
    name: "Proposta",
    subject: "Proposta — {{projeto}}",
    body: `Olá {{nome}},

Segue a proposta do projeto que conversamos, com escopo, prazo e investimento fechados.

Qualquer ajuste é só me dizer — fico à disposição para conversarmos.

Atenciosamente,
Eliveuton Souza`,
  },
  {
    id: "welcome",
    name: "Boas-vindas",
    subject: "Obrigado pelo contato — Eliveuton Souza",
    body: `Olá {{nome}},

Muito obrigado pelo seu contato! Recebi sua mensagem e estou analisando sua solicitação.

Retorno em breve com mais informações sobre como posso ajudar no seu projeto.

Atenciosamente,
Eliveuton Souza`,
  },
  {
    id: "follow-up",
    name: "Follow-up",
    subject: "Re: seu projeto — atualização",
    body: `Olá {{nome}},

Estou entrando em contato para saber se você ainda tem interesse em dar andamento ao projeto que conversamos.

Caso tenha alguma dúvida ou queira discutir mais detalhes, fico à disposição.

Atenciosamente,
Eliveuton Souza`,
  },
];

export function EmailComposerDialog({
  open,
  onOpenChange,
  lead,
  attachment,
}: EmailComposerDialogProps) {
  const fill = (s: string) =>
    s.split("{{nome}}").join(lead.name.split(" ")[0]).split("{{projeto}}").join("seu projeto");

  const [selectedTemplate, setSelectedTemplate] = useState(templates[0].id);
  const [subject, setSubject] = useState(fill(templates[0].subject));
  const [body, setBody] = useState(fill(templates[0].body));
  const [isSending, setIsSending] = useState(false);
  const sendLeadEmail = useSendLeadEmail();

  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplate(templateId);
    const template = templates.find((t) => t.id === templateId);
    if (template) {
      setSubject(fill(template.subject));
      setBody(fill(template.body));
    }
  };

  const handleSend = async () => {
    if (!lead.email) return;
    if (!subject.trim() || !body.trim()) {
      toast.error("Preencha o assunto e o corpo do e-mail");
      return;
    }

    setIsSending(true);
    try {
      const finalBody = attachment
        ? `${body.trimEnd()}\n\n${attachment.name}: ${attachment.url}`
        : body;

      // send-lead-email logs the interaction and stamps contacted_at server-side;
      // the hook invalidates lead/interactions/leads on success.
      await sendLeadEmail.mutateAsync({
        leadId: lead.id,
        to: lead.email,
        subject,
        body: finalBody,
      });

      toast.success("E-mail enviado e interação registrada!");
      onOpenChange(false);
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error(error instanceof Error ? error.message : "Erro ao enviar e-mail");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[620px] gap-0 overflow-hidden rounded-[18px] p-0">
        <DialogHeader className="border-b border-border px-6 pb-4 pt-[22px] text-left">
          <DialogTitle className="text-[17px] font-bold tracking-[-0.02em]">
            Novo e-mail
          </DialogTitle>
          <DialogDescription className="sr-only">Enviar e-mail para {lead.name}</DialogDescription>
        </DialogHeader>

        <div className="px-6">
          <div className="flex flex-wrap gap-[7px] border-b border-border py-3">
            {templates.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => handleTemplateChange(t.id)}
                className={cn(
                  "rounded-full border px-3 py-1.5 text-[11.5px] transition-colors",
                  selectedTemplate === t.id
                    ? "border-primary bg-accent font-bold text-primary"
                    : "border-border font-semibold text-muted-foreground hover:bg-muted",
                )}
              >
                {t.name}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 border-b border-border py-[13px] text-[13px]">
            <span className="w-14 shrink-0 text-muted-foreground">Para</span>
            {lead.email ? (
              <span className="truncate font-semibold">{lead.email}</span>
            ) : (
              <span className="font-semibold text-bad">
                Lead sem e-mail — adicione um em Editar lead para enviar.
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 border-b border-border py-1.5 text-[13px]">
            <span className="w-14 shrink-0 text-muted-foreground">Assunto</span>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Assunto do e-mail"
              className="h-8 border-0 bg-transparent px-0 text-[13px] font-semibold shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
            />
          </div>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Escreva o e-mail..."
            rows={9}
            className="min-h-[180px] resize-none border-0 bg-transparent px-0 py-[18px] text-[13.5px] leading-[1.7] text-muted-foreground shadow-none focus:text-foreground focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        </div>

        <div className="flex items-center gap-[9px] border-t border-border px-6 py-4">
          {attachment && (
            <a
              href={attachment.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-[7px] rounded-[9px] border border-border bg-muted px-[11px] py-[7px] text-[11.5px] font-semibold text-muted-foreground hover:text-foreground"
            >
              <Paperclip className="h-3 w-3" />
              {attachment.name}
            </a>
          )}
          <div className="flex-1" />
          <Button
            variant="outline"
            className="h-[38px] rounded-[10px] text-[12.5px] font-semibold"
            onClick={() => onOpenChange(false)}
          >
            Descartar
          </Button>
          <Button
            onClick={handleSend}
            disabled={isSending || !lead.email}
            className="h-[38px] rounded-[10px] text-[12.5px] font-semibold"
          >
            {isSending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Enviar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
